import { COMPONENT_DEFINITIONS } from "./component-definitions";
import { ComponentConfigMap, ComponentFactory, ComponentLoader } from "./types";

const OWN_SCRIPT_URL =
  typeof document !== "undefined"
    ? document.currentScript?.getAttribute("src")
    : undefined;

const siblingUrl = (file: string): string => {
  if (!OWN_SCRIPT_URL) {
    throw new Error(
      "Could not determine this SDK's own <script src> to load sibling bundles from."
    );
  }

  return OWN_SCRIPT_URL.replace(/[^/]+$/, file);
};

const pendingScriptLoads: Record<string, Promise<void>> = {};

const loadScriptOnce = (src: string): Promise<void> => {
  const pending = pendingScriptLoads[src];
  if (pending) return pending;

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => {
      delete pendingScriptLoads[src];
      reject(new Error(`Failed to load ${src}`));
    };
    document.head.appendChild(script);
  });

  pendingScriptLoads[src] = promise;
  return promise;
};

const loadFromUmdGlobal = async <Name extends keyof ComponentConfigMap>(
  name: Name
): Promise<ComponentFactory<Name>> => {
  const def = COMPONENT_DEFINITIONS[name];
  const globalScope = window as unknown as Record<string, unknown>;

  // Always load via createComponent -- merchants aren't expected to
  // preload sibling bundles themselves with this API (unlike the array-based
  // path's two-script-tag setup), so we don't defer to whatever's already on
  // `window`. loadScriptOnce still dedupes repeated calls for this URL.
  await loadScriptOnce(siblingUrl(`${def.category}.js`));

  const bundle = globalScope[def.umdGlobal] as
    | Record<string, ComponentFactory<Name>>
    | undefined;
  const factory = bundle?.[def.componentName];

  if (!factory) {
    throw new Error(
      `Expected window.${def.umdGlobal}.${def.componentName} after loading ` +
        `${def.category}.js, but it was not found.`
    );
  }

  return factory;
};

export const COMPONENT_LOADERS: {
  [Name in keyof ComponentConfigMap]: ComponentLoader<Name>;
} = Object.fromEntries(
  (Object.keys(COMPONENT_DEFINITIONS) as (keyof ComponentConfigMap)[]).map(
    (name) => [name, () => loadFromUmdGlobal(name)]
  )
) as { [Name in keyof ComponentConfigMap]: ComponentLoader<Name> };
